
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { logoutEmployee } from "../../redux/actions/authActions";
import { useLanguage } from "../../context/LanguageContext";
import LanguageSelector from ".././../context/LanguageSelector";
import { useSessionTimer } from "../../hooks/useSessionTimer"; // Import the hook

const ProfilePage = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useLanguage();
   useSessionTimer();
  useEffect(() => {
    const userDataString = localStorage.getItem("user");
    
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        const { name, phone } = userData;
        setProfileData({ name, phone });
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
    
    setLoading(false);
  }, []);

  const handleLogout = () => {
    dispatch(logoutEmployee());
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">{t.global.loading}</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-6">
        <LanguageSelector position="top-right" />
        <div className="max-w-md w-full">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-200/50 dark:border-gray-700/50 shadow-xl-soft text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <svg className="w-10 h-10 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 font-sans">
              {t.global.noProfileData}
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              {t.global.noUserData}
            </p>
            <button
              onClick={handleLogout}
              className="mt-6 w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-xl font-medium transition-colors duration-300 font-sans"
            >
              {t.global.logout}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 flex flex-col items-center justify-center p-6">
      <LanguageSelector position="top-right" />
      <div className="max-w-md w-full">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-200/50 dark:border-gray-700/50 shadow-xl-soft">
          {/* Profile Header */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center">
              <span className="text-white text-2xl font-bold">
                {profileData.name?.charAt(0) || "U"}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white font-sans">
              {profileData.name || t.global.notAvailable}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">{t.global.yourProfile}</p>
          </div>

          {/* Profile Details */}
          <div className="space-y-6">
            {/* Name Field */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{t.global.name}</p>
                  <p className="text-lg font-semibold text-gray-800 dark:text-white">
                    {profileData.name || t.global.notAvailable}
                  </p>
                </div>
              </div>
            </div>

            {/* Phone Field */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                  <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{t.global.phone}</p>
                  <p className="text-lg font-semibold text-gray-800 dark:text-white">
                    {profileData.phone || t.global.notAvailable}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Info Message */}
          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="ml-3 text-sm text-blue-600 dark:text-blue-400">
                {t.global.infoMessage}
              </p>
            </div>
          </div>

          {/* Logout Button */}
          <div className="mt-8">
            <button
              onClick={handleLogout}
              className="w-full group flex items-center justify-between bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-5 rounded-2xl border border-red-200/50 dark:border-red-800/50 shadow-xl-soft hover:shadow-2xl transition-all duration-300 hover:-translate-y-0.5"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center shadow-md">
                  <LogOut className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-100 font-sans">
                    {t.global.logout}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t.global.signOut}
                  </p>
                </div>
              </div>
              <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <LogOut className="w-4 h-4 text-red-600 dark:text-red-400" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;